import { Footer } from "@/components/layout/Footer";
import { Navbar } from "@/components/layout/Navbar";
import { ProductGrid } from "@/components/products/ProductGrid";
import { ProductsSearch } from "@/components/search/ProductsSearch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import {
	getCategoryNameById,
	useMainCategories,
	useSubcategories,
} from "@/hooks/use-categories";
import { useTags } from "@/hooks/use-tags";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency, getSupplierNameById } from "@/lib/utils";
import { isProduction } from "@/utils/environment";
import { Filter, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const Products = () => {
	const [loading, setLoading] = useState(true);
	const [products, setProducts] = useState([]);
	const [filteredProducts, setFilteredProducts] = useState([]);
	const { data: mainCategories } = useMainCategories();
	const [minPrice, setMinPrice] = useState(0);
	const [maxPrice, setMaxPrice] = useState(1000);
	const [highestPrice, setHighestPrice] = useState(1000);
	const [selectedCategories, setSelectedCategories] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [supplierNames, setSupplierNames] = useState({});
	const [selectedMainCategory, setSelectedMainCategory] = useState(null);
	const [selectedSubCategory, setSelectedSubCategory] = useState(null);
	const [selectedTags, setSelectedTags] = useState([]);
	const { data: allTags } = useTags();
	const [searchParams, setSearchParams] = useSearchParams();
	const [categoryMap, setCategoryMap] = useState({});
	const isProd = isProduction();

	const { data: subCategories } = useSubcategories(selectedMainCategory);

	useEffect(() => {
		const query = searchParams.get("q");
		if (query) {
			setSearchQuery(query);
		}

		const tag = searchParams.get("tag");
		if (tag) {
			setSelectedTags([tag]);
		}

		const category = searchParams.get("category");
		if (category) {
			setSelectedCategories([category]);
		}
	}, [searchParams]);

	useEffect(() => {
		const params = new URLSearchParams();

		if (searchQuery) {
			params.set("q", searchQuery);
		}

		if (selectedTags.length === 1) {
			params.set("tag", selectedTags[0]);
		}

		if (selectedCategories.length === 1) {
			params.set("category", selectedCategories[0]);
		}

		setSearchParams(params, { replace: true });
	}, [searchQuery, selectedTags, selectedCategories, setSearchParams]);

	// Fetch all categories and build parent-child relationships
	useEffect(() => {
		const fetchCategoryMap = async () => {
			const { data: categories, error } = await supabase
				.from("categories")
				.select("*");

			if (error) {
				console.error("Error fetching categories:", error);
				return;
			}

			// Build a map of subcategory ID -> parent category ID
			const subToMainMap = {};
			categories.forEach((category) => {
				if (category.parent_id) {
					subToMainMap[category.id] = category.parent_id;
				}
			});

			setCategoryMap(subToMainMap);
		};

		fetchCategoryMap();
	}, []);

	useEffect(() => {
		const fetchProducts = async () => {
			try {
				setLoading(true);

				let query = supabase.from("products").select(`
						*,
						product_images(*),
						supplier:supplier_id(
							id,
							business_name,
							name,
							is_test
						)
					`);

				if (searchQuery) {
					const { data: nameDescProducts, error: nameDescError } =
						await supabase
							.from("products")
							.select("id")
							.or(
								`name.ilike.%${searchQuery}%, description.ilike.%${searchQuery}%`
							);

					if (nameDescError) throw nameDescError;

					const { data: suppliers, error: supplierError } =
						await supabase
							.from("profiles")
							.select("id")
							.or(
								`name.ilike.%${searchQuery}%, business_name.ilike.%${searchQuery}%`
							);

					if (supplierError) throw supplierError;

					const supplierIds = suppliers.map((s) => s.id);
					let supplierProducts = [];

					if (supplierIds.length > 0) {
						const { data: products, error } = await supabase
							.from("products")
							.select("id")
							.in("supplier_id", supplierIds);

						if (!error) {
							supplierProducts = products;
						}
					}

					const { data: tags, error: tagsError } = await supabase
						.from("tags")
						.select("id")
						.ilike("name", `%${searchQuery}%`);

					if (tagsError) throw tagsError;

					const tagIds = tags.map((t) => t.id);
					let tagProducts = [];

					if (tagIds.length > 0) {
						const { data: products, error } = await supabase
							.from("product_tags")
							.select("product_id")
							.in("tag_id", tagIds);

						if (!error) {
							tagProducts = products.map((p) => ({
								id: p.product_id,
							}));
						}
					}

					const { data: ingredients, error: ingredientsError } =
						await supabase
							.from("ingredients")
							.select("id")
							.ilike("name", `%${searchQuery}%`);

					if (ingredientsError) throw ingredientsError;

					const ingredientIds = ingredients.map((i) => i.id);
					let ingredientProducts = [];

					if (ingredientIds.length > 0) {
						const { data: products, error } = await supabase
							.from("product_ingredients")
							.select("product_id")
							.in("ingredient_id", ingredientIds);

						if (!error) {
							ingredientProducts = products.map((p) => ({
								id: p.product_id,
							}));
						}
					}

					const searchProductIds = [
						...nameDescProducts,
						...supplierProducts,
						...tagProducts,
						...ingredientProducts,
					].map((p) => p.id);

					const uniqueProductIds = [...new Set(searchProductIds)];

					if (uniqueProductIds.length > 0) {
						query = query.in("id", uniqueProductIds);
					} else {
						setProducts([]);
						setFilteredProducts([]);
						setLoading(false);
						return;
					}
				}

				const { data: products, error } = await query.order(
					"created_at",
					{ ascending: false }
				);

				if (error) throw error;

				// Add filtering based on supplier test status
				const filteredProducts = isProd
					? products.filter((p) => !p.supplier?.is_test)
					: products.filter((p) => p.supplier?.is_test);

				const supplierIds = [
					...new Set(filteredProducts.map((p) => p.supplier_id)),
				];
				const supplierNameMap = {};

				await Promise.all(
					supplierIds.map(async (id) => {
						const name = await getSupplierNameById(id);
						supplierNameMap[id] = name;
					})
				);

				setSupplierNames(supplierNameMap);

				const formattedProducts = await Promise.all(
					filteredProducts.map(async (product) => {
						const primaryImage = product.product_images.find(
							(img) => img.is_primary
						);
						const imageUrl =
							primaryImage?.image_url ||
							product.product_images[0]?.image_url ||
							"/placeholder.svg";

						const categoryName = await getCategoryNameById(
							product.category_id
						);

						const { data: tagsData } = await supabase
							.from("product_tags")
							.select("tags(id, name)")
							.eq("product_id", product.id);

						const tags = tagsData
							? tagsData.map((item) => ({
									id: item.tags.id,
									name: item.tags.name,
							  }))
							: [];

						const { data: ingredientsData } = await supabase
							.from("product_ingredients")
							.select("ingredients(id, name)")
							.eq("product_id", product.id);

						const ingredients = ingredientsData
							? ingredientsData.map((item) => ({
									id: item.ingredients.id,
									name: item.ingredients.name,
							  }))
							: [];

						return {
							id: product.id,
							name: product.name,
							brand:
								supplierNameMap[product.supplier_id] ||
								"Unknown Supplier",
							description: product.description || "",
							price: product.price,
							minOrder: 1,
							image: imageUrl,
							category: categoryName,
							categoryId: product.category_id,
							tags: tags,
							ingredients: ingredients,
							supplierId: product.supplier_id,
							msrp: product.msrp || undefined,
							caseQuantity: product.case_quantity || 1,
							isTestSupplier: product.supplier?.is_test || false,
						};
					})
				);

				setProducts(formattedProducts);
				setFilteredProducts(formattedProducts);

				const prices = formattedProducts.map((p) => p.price);
				if (prices.length > 0) {
					const maxProductPrice =
						Math.ceil(Math.max(...prices) / 100) * 100;
					setMaxPrice(maxProductPrice);
					setHighestPrice(maxProductPrice);
				}
			} catch (error) {
				console.error("Error fetching products:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchProducts();
	}, [searchQuery, isProd]);

	useEffect(() => {
		let filtered = [...products];

		if (selectedCategories.length > 0) {
			filtered = filtered.filter((product) => {
				// Direct match with the selected category
				if (selectedCategories.includes(product.categoryId)) {
					return true;
				}

				// For main category selection, check if the product's category is a subcategory of it
				if (selectedMainCategory && !selectedSubCategory) {
					// If we're filtering by a main category, include products from its subcategories
					const productCategoryParentId =
						categoryMap[product.categoryId];
					return selectedCategories.includes(productCategoryParentId);
				}

				return false;
			});
		}

		if (selectedTags.length > 0) {
			filtered = filtered.filter((product) =>
				product.tags.some((tag) => selectedTags.includes(tag.id))
			);
		}

		filtered = filtered.filter(
			(product) => product.price >= minPrice && product.price <= maxPrice
		);

		setFilteredProducts(filtered);
	}, [
		selectedCategories,
		selectedTags,
		minPrice,
		maxPrice,
		products,
		categoryMap,
		selectedMainCategory,
		selectedSubCategory,
	]);

	const resetFilters = () => {
		setSearchQuery("");
		setSelectedCategories([]);
		setSelectedTags([]);
		setMinPrice(0);
		setMaxPrice(highestPrice);
		setSelectedMainCategory(null);
		setSelectedSubCategory(null);
		setSearchParams({});
	};

	const handleTagToggle = (tagId) => {
		setSelectedTags((prevSelectedTags) =>
			prevSelectedTags.includes(tagId)
				? prevSelectedTags.filter((id) => id !== tagId)
				: [...prevSelectedTags, tagId]
		);
	};

	const handleSearch = (query) => {
		setSearchQuery(query);
	};

	return (
		<div className="min-h-screen flex flex-col">
			<Navbar />

			<main className="flex-grow pt-20">
				<div className="container max-w-6xl mx-auto px-6 py-8">
					<div className="flex justify-between items-center mb-8">
						<h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
							All Products
						</h1>

						<div className="relative w-full max-w-sm ml-auto">
							<ProductsSearch
								onSearch={handleSearch}
								initialQuery={searchQuery}
							/>
						</div>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-[250px_1fr] gap-8">
						<div className="space-y-6 self-start sticky top-24">
							<div className="bg-white p-5 rounded-lg border">
								<div className="flex items-center justify-between mb-4">
									<h2 className="font-semibold flex items-center">
										<Filter className="mr-2 h-4 w-4" />
										Filters
									</h2>
									<Button
										variant="ghost"
										size="sm"
										onClick={resetFilters}
										title="Reset filters"
										className="h-8 px-2">
										<RotateCcw className="h-3.5 w-3.5 mr-1" />
										Reset
									</Button>
								</div>

								<div className="space-y-5">
									<div>
										<Label className="text-sm font-medium">
											Categories
										</Label>

										<div className="mt-1.5 space-y-2">
											{mainCategories?.map((category) => (
												<div key={category.id}>
													<div className="flex items-center">
														<input
															type="checkbox"
															id={`category-${category.id}`}
															checked={
																selectedMainCategory ===
																category.id
															}
															onChange={(e) => {
																if (
																	e.target
																		.checked
																) {
																	setSelectedMainCategory(
																		category.id
																	);
																	setSelectedSubCategory(
																		null
																	);
																	setSelectedCategories(
																		[
																			category.id,
																		]
																	);
																} else {
																	setSelectedMainCategory(
																		null
																	);
																	setSelectedSubCategory(
																		null
																	);
																	setSelectedCategories(
																		[]
																	);
																}
															}}
															className="h-4 w-4 rounded border-gray-300"
														/>
														<label
															htmlFor={`category-${category.id}`}
															className="ml-2 text-sm text-gray-600">
															{category.name}
														</label>
													</div>

													{selectedMainCategory ===
														category.id &&
														subCategories?.length >
															0 && (
															<div className="mt-2 ml-6 space-y-2 border-l-2 pl-4">
																{subCategories.map(
																	(
																		subCategory
																	) => (
																		<div
																			key={
																				subCategory.id
																			}
																			className="flex items-center">
																			<input
																				type="checkbox"
																				id={`category-${subCategory.id}`}
																				checked={
																					selectedSubCategory ===
																					subCategory.id
																				}
																				onChange={(
																					e
																				) => {
																					if (
																						e
																							.target
																							.checked
																					) {
																						setSelectedSubCategory(
																							subCategory.id
																						);
																						setSelectedCategories(
																							[
																								subCategory.id,
																							]
																						);
																					} else {
																						setSelectedSubCategory(
																							null
																						);
																						setSelectedCategories(
																							[
																								selectedMainCategory,
																							]
																						);
																					}
																				}}
																				className="h-4 w-4 rounded border-gray-300"
																			/>
																			<label
																				htmlFor={`category-${subCategory.id}`}
																				className="ml-2 text-sm text-gray-600">
																				{
																					subCategory.name
																				}
																			</label>
																		</div>
																	)
																)}
															</div>
														)}
												</div>
											))}
										</div>
									</div>

									<div>
										<Label className="text-sm font-medium">
											Product Tags
										</Label>
										<div className="mt-2 flex flex-wrap gap-2">
											{allTags?.map((tag) => (
												<Button
													key={tag.id}
													variant={
														selectedTags.includes(
															tag.id
														)
															? "default"
															: "outline"
													}
													size="sm"
													className="text-xs h-7"
													onClick={() =>
														handleTagToggle(tag.id)
													}>
													{tag.name}
												</Button>
											))}
										</div>
									</div>

									<div>
										<Label className="text-sm font-medium">
											Price Range
										</Label>
										<div className="space-y-4">
											<div>
												<Label
													htmlFor="min-price"
													className="text-xs text-gray-500">
													Min Price
												</Label>
												<div className="flex gap-4 items-center mt-1">
													<Input
														id="min-price"
														type="number"
														min={0}
														max={maxPrice}
														value={minPrice}
														onChange={(e) => {
															const value =
																Number(
																	e.target
																		.value
																);
															if (
																value >= 0 &&
																value <=
																	maxPrice
															) {
																setMinPrice(
																	value
																);
															}
														}}
														className="w-24"
													/>
													<div className="flex-1">
														<Slider
															defaultValue={[
																minPrice,
															]}
															min={0}
															max={maxPrice}
															step={10}
															value={[minPrice]}
															onValueChange={([
																value,
															]) => {
																if (
																	value <=
																	maxPrice
																) {
																	setMinPrice(
																		value
																	);
																}
															}}
														/>
													</div>
												</div>
											</div>

											<div>
												<Label
													htmlFor="max-price"
													className="text-xs text-gray-500">
													Max Price
												</Label>
												<div className="flex gap-4 items-center mt-1">
													<Input
														id="max-price"
														type="number"
														min={minPrice}
														max={highestPrice}
														value={maxPrice}
														onChange={(e) => {
															const value =
																Number(
																	e.target
																		.value
																);
															if (
																value >=
																	minPrice &&
																value <=
																	highestPrice
															) {
																setMaxPrice(
																	value
																);
															}
														}}
														className="w-24"
													/>
													<div className="flex-1">
														<Slider
															defaultValue={[
																maxPrice,
															]}
															min={minPrice}
															max={highestPrice}
															step={10}
															value={[maxPrice]}
															onValueChange={([
																value,
															]) => {
																if (
																	value >=
																	minPrice
																) {
																	setMaxPrice(
																		value
																	);
																}
															}}
														/>
													</div>
												</div>
											</div>

											<div className="flex items-center justify-between text-sm text-gray-500 px-2">
												<span>
													{formatCurrency(minPrice)}
												</span>
												<span>
													{formatCurrency(maxPrice)}
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div>
							{loading ? (
								<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
									{[...Array(6)].map((_, i) => (
										<div
											key={i}
											className="bg-white rounded-lg overflow-hidden border">
											<Skeleton className="h-48 w-full" />
											<div className="p-4 space-y-3">
												<Skeleton className="h-6 w-3/4" />
												<Skeleton className="h-4 w-1/2" />
												<Skeleton className="h-5 w-1/3" />
											</div>
										</div>
									))}
								</div>
							) : filteredProducts.length > 0 ? (
								<ProductGrid products={filteredProducts} />
							) : (
								<div className="text-center py-12">
									<h3 className="text-xl font-medium mb-2">
										No products found
									</h3>
									<p className="text-gray-500 mb-6">
										Try adjusting your search or filter
										criteria
									</p>
									<Button onClick={resetFilters}>
										Reset Filters
									</Button>
								</div>
							)}
						</div>
					</div>
				</div>
				{!isProd && (
					<section className="py-6 bg-amber-50">
						<div className="container max-w-6xl mx-auto px-6 md:px-10 text-center">
							<div className="inline-flex items-center gap-2 text-amber-800 text-sm font-medium px-3 py-1 bg-amber-100 rounded">
								<span>Test Environment</span>
							</div>
							<p className="text-amber-700 mt-1">
								You are viewing test suppliers and products
							</p>
						</div>
					</section>
				)}
			</main>

			<Footer />
		</div>
	);
};

export default Products;
